import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ProvincesService from '../services/ProvincesService';
import API from '../services/API';

const CustomerInfoPage = () => {
    const { phone } = useParams();
    const [formData, setFormData] = useState({
        fullName: '',
        province: '',
        district: '',
        ward: '',
        streetAddress: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [userInfo, setUserInfo] = useState(null);
    const [pendingUserData, setPendingUserData] = useState(null); // Store user data until provinces are loaded
    
    // State for dynamic data loading
    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [wards, setWards] = useState([]);
    const [loadingProvinces, setLoadingProvinces] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [loadingWards, setLoadingWards] = useState(false);

    // Load user information when component mounts
    useEffect(() => {
        const loadUserInfo = async () => {
            if (!phone) {
                console.error('No phone number provided in URL');
                setInitialLoading(false);
                return;
            }

            try {
                console.log('Loading user info for phone:', phone);
                
                // Call login-with-phone API to get user info
                const userCheck = await API.checkUserExists(phone);
                console.log('User check result:', userCheck);
                
                if (userCheck.exists) {
                    const basicUserData = userCheck.userInfo || {};
                    
                    // Store access token for API calls
                    if (userCheck.accessToken) {
                        sessionStorage.setItem('accessToken', userCheck.accessToken);
                        
                        // Get detailed user profile using access token
                        try {
                            console.log('Getting detailed user profile with access token...');
                            const detailedUserInfo = await API.getUserInfo(phone);
                            console.log('Detailed user profile:', detailedUserInfo);
                            
                            // Extract userData from the response structure
                            const profileData = detailedUserInfo?.userData || detailedUserInfo || {};
                            console.log('Extracted profile data:', profileData);
                            
                            // Merge basic and detailed user info
                            const completeUserData = {
                                ...basicUserData,
                                ...profileData
                            };
                            
                            setUserInfo(completeUserData);
                            
                            // Store user data to be processed after provinces are loaded
                            // Handle different name field combinations from API
                            let fullName = '';
                            if (completeUserData.name) {
                                fullName = completeUserData.name;
                            } else if (completeUserData.fullName) {
                                fullName = completeUserData.fullName;
                            } else if (completeUserData.firstName || completeUserData.lastName) {
                                const firstName = completeUserData.firstName || '';
                                const lastName = completeUserData.lastName || '';
                                fullName = `${firstName} ${lastName}`.trim();
                            }
                            
                            console.log('Processed fullName:', fullName);
                            
                            const userData = {
                                fullName: fullName,
                                province: completeUserData.city || completeUserData.province || '',
                                district: completeUserData.district || '',
                                ward: completeUserData.ward || '',
                                streetAddress: completeUserData.address || completeUserData.streetAddress || '',
                                phone: completeUserData.phone || phone
                            };
                            
                            console.log('Final userData for form:', userData);
                            console.log('Address fields:', {
                                city: completeUserData.city,
                                province: completeUserData.province,
                                district: completeUserData.district,
                                ward: completeUserData.ward,
                                address: completeUserData.address,
                                streetAddress: completeUserData.streetAddress
                            });
                            
                            setPendingUserData(userData);
                        } catch (profileError) {
                            console.error('Error getting detailed profile, using basic info:', profileError);
                            // Fallback to basic user info if profile API fails
                            setUserInfo(basicUserData);
                            
                            const userData = {
                                fullName: basicUserData.name || basicUserData.fullName || '',
                                province: basicUserData.city || basicUserData.province || '',
                                district: basicUserData.district || '',
                                ward: basicUserData.ward || '',
                                streetAddress: basicUserData.address || basicUserData.streetAddress || '',
                                phone: basicUserData.phone || phone
                            };
                            
                            setPendingUserData(userData);
                        }
                    } else {
                        // No access token, use basic info only
                        console.log('No access token available, using basic info only');
                        setUserInfo(basicUserData);
                        
                        const userData = {
                            fullName: basicUserData.name || basicUserData.fullName || '',
                            province: basicUserData.city || basicUserData.province || '',
                            district: basicUserData.district || '',
                            ward: basicUserData.ward || '',
                            streetAddress: basicUserData.address || basicUserData.streetAddress || '',
                            phone: basicUserData.phone || phone
                        };
                        
                        setPendingUserData(userData);
                    }
                } else {
                    // User not found, still allow form filling
                    console.log('User not found, allowing manual entry');
                    setPendingUserData({
                        fullName: '',
                        province: '',
                        district: '',
                        ward: '',
                        streetAddress: '',
                        phone: phone
                    });
                }
            } catch (error) {
                console.error('Error loading user info:', error);
                // Even if API fails, allow manual entry with phone number
                setPendingUserData({
                    fullName: '',
                    province: '',
                    district: '',
                    ward: '',
                    streetAddress: '',
                    phone: phone
                });
            } finally {
                setInitialLoading(false);
            }
        };

        loadUserInfo();
    }, [phone]);

    // Load provinces when component mounts
    useEffect(() => {
        const loadProvinces = async () => {
            setLoadingProvinces(true);
            try {
                const provincesData = await ProvincesService.getProvinces();
                setProvinces(provincesData);
            } catch (error) {
                console.error('Error loading provinces:', error);
                // Fallback to hardcoded list if API fails
                const fallbackProvinces = [
                    'Hà Nội', 'TP. Hồ Chí Minh', 'Đà Nẵng', 'Hải Phòng', 'Cần Thơ',
                    'An Giang', 'Bà Rịa - Vũng Tàu', 'Bắc Giang', 'Bắc Kạn', 'Bạc Liêu',
                    'Bắc Ninh', 'Bến Tre', 'Bình Định', 'Bình Dương', 'Bình Phước',
                    'Bình Thuận', 'Cà Mau', 'Cao Bằng', 'Đắk Lắk', 'Đắk Nông',
                    'Điện Biên', 'Đồng Nai', 'Đồng Tháp', 'Gia Lai', 'Hà Giang',
                    'Hà Nam', 'Hà Tĩnh', 'Hải Dương', 'Hậu Giang', 'Hòa Bình',
                    'Hưng Yên', 'Khánh Hòa', 'Kiên Giang', 'Kon Tum', 'Lai Châu',
                    'Lâm Đồng', 'Lạng Sơn', 'Lào Cai', 'Long An', 'Nam Định',
                    'Nghệ An', 'Ninh Bình', 'Ninh Thuận', 'Phú Thọ', 'Phú Yên',
                    'Quảng Bình', 'Quảng Nam', 'Quảng Ngãi', 'Quảng Ninh', 'Quảng Trị',
                    'Sóc Trăng', 'Sơn La', 'Tây Ninh', 'Thái Bình', 'Thái Nguyên',
                    'Thanh Hóa', 'Thừa Thiên Huế', 'Tiền Giang', 'Trà Vinh', 'Tuyên Quang',
                    'Vĩnh Long', 'Vĩnh Phúc', 'Yên Bái'
                ].map(name => ({ name, code: null }));
                setProvinces(fallbackProvinces);
            } finally {
                setLoadingProvinces(false);
            }
        };

        loadProvinces();
    }, []);

    // Function to load location data cascade (province -> district -> ward)
    const loadLocationCascade = useCallback(async (provinceName, districtName, wardName) => {
        console.log('Loading location cascade:', { provinceName, districtName, wardName });
        
        if (!provinceName || provinces.length === 0) {
            return;
        }

        // Find and load districts for province
        const selectedProvince = provinces.find(p => p.name === provinceName);
        if (selectedProvince && selectedProvince.code) {
            setLoadingDistricts(true);
            try {
                const districtsData = await ProvincesService.getDistrictsByProvince(selectedProvince.code);
                setDistricts(districtsData);
                console.log('Loaded districts for province:', provinceName, districtsData.length);
                
                // If we have district name, load wards for that district
                if (districtName && districtsData.length > 0) {
                    const selectedDistrict = districtsData.find(d => d.name === districtName);
                    if (selectedDistrict && selectedDistrict.code) {
                        setLoadingWards(true);
                        try {
                            const wardsData = await ProvincesService.getWardsByDistrict(selectedDistrict.code);
                            setWards(wardsData);
                            console.log('Loaded wards for district:', districtName, wardsData.length);
                        } catch (error) {
                            console.error('Error loading wards:', error);
                            setWards([]);
                        } finally {
                            setLoadingWards(false);
                        }
                    } else {
                        console.warn('District not found in loaded data:', districtName);
                        setWards([]);
                    }
                } else {
                    setWards([]);
                }
            } catch (error) {
                console.error('Error loading districts:', error);
                setDistricts([]);
                setWards([]);
            } finally {
                setLoadingDistricts(false);
            }
        } else {
            console.warn('Province not found in loaded data:', provinceName);
            setDistricts([]);
            setWards([]);
        }
    }, [provinces]);

    // Process pending user data when provinces are loaded
    useEffect(() => {
        if (provinces.length > 0 && pendingUserData && !formData.fullName) {
            console.log('Processing pending user data:', pendingUserData);
            setFormData(pendingUserData);
            setPendingUserData(null); // Clear pending data
        }
    }, [provinces, pendingUserData, formData.fullName]);

    // Load location data when provinces are loaded and we have user location info
    useEffect(() => {
        if (provinces.length > 0 && formData.province) {
            loadLocationCascade(formData.province, formData.district, formData.ward);
        }
    }, [provinces, formData.province, formData.district, formData.ward, loadLocationCascade]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Vui lòng nhập họ và tên';
        }

        if (!formData.province.trim()) {
            newErrors.province = 'Vui lòng chọn tỉnh/thành phố';
        }

        if (!formData.district.trim()) {
            newErrors.district = 'Vui lòng chọn quận/huyện';
        }

        if (!formData.ward.trim()) {
            newErrors.ward = 'Vui lòng chọn phường/xã';
        }

        if (!formData.streetAddress.trim()) {
            newErrors.streetAddress = 'Vui lòng nhập địa chỉ chi tiết';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Vui lòng nhập số điện thoại nhận quà';
        } else if (formData.phone.trim().length < 10) {
            newErrors.phone = 'Số điện thoại phải có ít nhất 10 số';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = async (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }

        // Handle cascade loading for province/district/ward
        if (field === 'province') {
            // Clear district and ward when province changes
            setFormData(prev => ({
                ...prev,
                district: '',
                ward: ''
            }));
            setDistricts([]);
            setWards([]);

            // Load districts for selected province
            if (value) {
                const selectedProvince = provinces.find(p => p.name === value);
                if (selectedProvince && selectedProvince.code) {
                    setLoadingDistricts(true);
                    try {
                        const districtsData = await ProvincesService.getDistrictsByProvince(selectedProvince.code);
                        setDistricts(districtsData);
                    } catch (error) {
                        console.error('Error loading districts:', error);
                        setDistricts([]);
                    } finally {
                        setLoadingDistricts(false);
                    }
                }
            }
        } else if (field === 'district') {
            // Clear ward when district changes
            setFormData(prev => ({
                ...prev,
                ward: ''
            }));
            setWards([]);

            // Load wards for selected district
            if (value) {
                const selectedDistrict = districts.find(d => d.name === value);
                if (selectedDistrict && selectedDistrict.code) {
                    setLoadingWards(true);
                    try {
                        const wardsData = await ProvincesService.getWardsByDistrict(selectedDistrict.code);
                        setWards(wardsData);
                    } catch (error) {
                        console.error('Error loading wards:', error);
                        setWards([]);
                    } finally {
                        setLoadingWards(false);
                    }
                }
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            console.log('Submitting customer info form data:', formData);
            
            // Save address using the same API as AddressForm
            const result = await API.saveUserAddress(phone, formData);
            
            if (result.success) {
                alert('Thông tin đã được cập nhật thành công!');
                // Optionally redirect or show success message
            } else {
                throw new Error('Không thể lưu thông tin');
            }
        } catch (error) {
            console.error('Error submitting customer info:', error);
            alert('Có lỗi xảy ra khi lưu thông tin: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Đang tải thông tin khách hàng...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#f0f9f4' }}>
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="24" 
                                height="24" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                style={{ color: '#277f4d' }}
                            >
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800">Thông tin khách hàng</h2>
                        <p className="text-gray-600 mt-1">
                            {userInfo ? 
                                'Cập nhật thông tin cá nhân và địa chỉ của bạn' : 
                                'Vui lòng nhập thông tin cá nhân và địa chỉ'
                            }
                        </p>
                        <p className="text-sm text-gray-500 mt-1">SĐT: {phone}</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Họ và tên */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Họ và tên *
                            </label>
                            <input
                                type="text"
                                value={formData.fullName}
                                onChange={(e) => handleInputChange('fullName', e.target.value)}
                                placeholder="Ví dụ: Nguyễn Văn A"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.fullName ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                            />
                            {errors.fullName && (
                                <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
                            )}
                        </div>

                        {/* Tỉnh/Thành phố */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tỉnh/Thành phố *
                            </label>
                            <select
                                value={formData.province}
                                onChange={(e) => handleInputChange('province', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.province ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                                disabled={loadingProvinces}
                            >
                                <option value="">
                                    {loadingProvinces ? 'Đang tải...' : 'Chọn tỉnh/thành phố'}
                                </option>
                                {provinces.map((province) => (
                                    <option key={province.code || province.name} value={province.name}>
                                        {province.name}
                                    </option>
                                ))}
                            </select>
                            {errors.province && (
                                <p className="text-red-500 text-xs mt-1">{errors.province}</p>
                            )}
                        </div>

                        {/* Quận/Huyện */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quận/Huyện *
                            </label>
                            <select
                                value={formData.district}
                                onChange={(e) => handleInputChange('district', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.district ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                                disabled={!formData.province || loadingDistricts}
                            >
                                <option value="">
                                    {loadingDistricts ? 'Đang tải...' : !formData.province ? 'Vui lòng chọn tỉnh/thành phố trước' : 'Chọn quận/huyện'}
                                </option>
                                {districts.map((district) => (
                                    <option key={district.code} value={district.name}>
                                        {district.name}
                                    </option>
                                ))}
                            </select>
                            {errors.district && (
                                <p className="text-red-500 text-xs mt-1">{errors.district}</p>
                            )}
                        </div>

                        {/* Phường/Xã */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phường/Xã *
                            </label>
                            <select
                                value={formData.ward}
                                onChange={(e) => handleInputChange('ward', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.ward ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                                disabled={!formData.district || loadingWards}
                            >
                                <option value="">
                                    {loadingWards ? 'Đang tải...' : !formData.district ? 'Vui lòng chọn quận/huyện trước' : 'Chọn phường/xã'}
                                </option>
                                {wards.map((ward) => (
                                    <option key={ward.code} value={ward.name}>
                                        {ward.name}
                                    </option>
                                ))}
                            </select>
                            {errors.ward && (
                                <p className="text-red-500 text-xs mt-1">{errors.ward}</p>
                            )}
                        </div>

                        {/* Địa chỉ chi tiết */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Địa chỉ chi tiết *
                            </label>
                            <input
                                type="text"
                                value={formData.streetAddress}
                                onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                                placeholder="Ví dụ: 123 Đường Nguyễn Huệ, Quận 1"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.streetAddress ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                            />
                            {errors.streetAddress && (
                                <p className="text-red-500 text-xs mt-1">{errors.streetAddress}</p>
                            )}
                        </div>

                        {/* Số điện thoại nhận quà */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Số điện thoại liên hệ *
                            </label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                placeholder="Ví dụ: 0123456789"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.phone ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                            />
                            {errors.phone && (
                                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                            )}
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: '#277f4d' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#1f5f3a'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#277f4d'}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Đang lưu...
                                    </div>
                                ) : (
                                    'Cập nhật thông tin'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CustomerInfoPage;
